import React, { useState } from 'react';
import emailjs from 'emailjs-com';
import ReCAPTCHA from 'react-google-recaptcha';
import {
    Box,
    Typography,
    Grid,
    TextField,
    MenuItem,
    Container,
    Paper,
    Stack,
    Snackbar,
    Alert,
    CircularProgress,
    Button
} from '@mui/material';
import { useLanguage } from "../../i18n/LanguageContext";
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SendIcon from '@mui/icons-material/Send';
import PageHeader from '../ui/PageHeader';

const Contact = () => {
    const { t, lang } = useLanguage();

    const [formData, setFormData] = useState({
        name: '',
        email2: '',
        tel: '',
        Adresse: '',
        message: '',
        site: '',
    });

    const [captchaToken, setCaptchaToken] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const contactMethods = [
        {
            id: 1,
            title: t('contact.tel_title'),
            value: "+216 74 433 020",
            icon: <PhoneInTalkOutlinedIcon />,
            color: '#1D2558'
        },
        {
            id: 2,
            title: "Email",
            value: "gm@polysoftco.tn",
            icon: <EmailOutlinedIcon />,
            color: '#1D2558'
        },
        {
            id: 3,
            title: t('contact.address_title'),
            value: t('contact.address_val'),
            icon: <LocationOnOutlinedIcon />,
            color: '#1D2558'
        }
    ];

    const softwareOptions = [
        { value: "24-crm", label: "24-crm" },
        { value: "24-Paie", label: "24-Paie" },
        { value: "24-Gold", label: "24-Gold" },
        { value: "gestion-commerciale", label: "Gestion commerciale" },
        { value: "e-commerce", label: "E-commerce" },
        { value: "PolyCompta", label: "PolyCompta" },
        { value: "Venus Optic", label: "Venus Optic" },
        { value: "PolyTrésor", label: "PolyTrésor" },
        { value: "PolyPointage", label: "PolyPointage" },
        { value: "PVTactile", label: "PVTactile" },
        { value: "GenRS", label: "GenRS" },
        { value: "autre", label: "Autre ..." }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.name || !formData.email2 || !formData.message) {
            setSnackbar({ open: true, message: t('common.fillFields'), severity: 'warning' });
            return;
        }
        if (!captchaToken) {
            setSnackbar({ open: true, message: t('common.completeCaptcha'), severity: 'warning' });
            return;
        }

        setIsSubmitting(true);

        const emailParams = {
            from_name: formData.name,
            tel: formData.tel,
            Adresse: formData.Adresse,
            email2: formData.email2,
            message: formData.message,
            site: formData.site,
        };

        emailjs.send('service_u3i7xfs', 'template_jah4wpf', emailParams, 'aftmeehiwnH-ym2V8')
            .then(() => {
              //  setFormData({ name: '', tel: '', Adresse: '', email2: '', message: '', site: '' });
              setFormData({ name: formData.name,tel: formData.tel,Adresse: formData.Adresse, email2: formData.email2,
                message: formData.message, site: formData.site });
              setSnackbar({ open: true, message: t('common.success'), severity: 'success' });
            })
            .catch(() => {
                setSnackbar({ open: true, message: t('common.error'), severity: 'error' });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <Box sx={{ bgcolor: '#fbfcfd' }}>
            <PageHeader
                title={t('contact.title')}
                subtitle={t('contact.subtitle')}
            />

            <Container maxWidth="lg" sx={{ mt: -10, mb: 15, position: 'relative', zIndex: 2 }}>
                <Grid container spacing={5}>
                    {/* Left Side: Contact Info */}
                    <Grid item xs={12} lg={5}>
                        <Stack spacing={3}>
                            {contactMethods.map((method) => (
                                <Paper
                                    key={method.id}
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        borderRadius: 6,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 3,
                                        bgcolor: '#ffffff',
                                        border: '1px solid rgba(6, 57, 125, 0.08)',
                                        transition: 'all 0.4s ease',
                                        '&:hover': {
                                            boxShadow: '0 25px 50px rgba(6, 57, 125, 0.08)',
                                            transform: 'translateY(-8px)',
                                            borderColor: '#DD8748'
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '20px',
                                        bgcolor: '#f4f7fa',
                                        color: '#1D2558',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid rgba(6, 57, 125, 0.05)'
                                    }}>
                                        {method.icon}
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                            {method.title}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={800} color="primary" sx={{ mt: 0.5 }}>
                                            {method.value}
                                        </Typography>
                                    </Box>
                                </Paper>
                            ))}
                        </Stack>
                    </Grid>

                    {/* Right Side: Professional Form */}
                    <Grid item xs={12} lg={7}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 4, md: 8 },
                                borderRadius: 8,
                                bgcolor: '#ffffff',
                                border: '1px solid rgba(6, 57, 125, 0.05)',
                                boxShadow: '0 50px 100px rgba(6, 57, 125, 0.08)'
                            }}
                        >
                            <Box sx={{ mb: 6 }}>
                                <Typography variant="h3" fontWeight={900} color="primary" sx={{ mb: 2, letterSpacing: -1 }}>
                                    {lang === 'ar' ? 'أرسل لنا رسالة' : 'Écrivez-nous'}
                                </Typography>
                                <Box sx={{ width: 60, height: 4, bgcolor: '#DD8748', borderRadius: 2 }} />
                            </Box>

                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t('contact.form.name')}
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={t('contact.form.tel')}
                                            name="tel"
                                            value={formData.tel}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} >
                                        <TextField
                                            fullWidth
                                            label={t('contact.form.address')}
                                            name="Adresse"
                                            value={formData.Adresse}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('contact.form.email')}
                                            type="email"
                                            name="email2"
                                            value={formData.email2}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            select
                                            label={t('contact.form.software')}
                                            name="site"
                                            value={formData.site}
                                            onChange={handleChange}
                                        >
                                            {softwareOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={5}
                                            label={t('contact.form.message')}
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ transform: { xs: 'scale(0.85)', sm: 'scale(1)' }, transformOrigin: 'left' }}>
                                            <ReCAPTCHA
                                                sitekey="6LeAA2sqAAAAAMriCtN3sts53MmmilM0WXY2xFEI"
                                                onChange={handleCaptchaChange}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={isSubmitting}
                                            sx={{
                                                py: 2.5,
                                                borderRadius: '16px',
                                                fontWeight: 900,
                                                fontSize: '1.2rem',
                                                bgcolor: '#1D2558',
                                                color: '#fff',
                                                textTransform: 'none',
                                                boxShadow: '0 10px 20px rgba(6, 57, 125, 0.2)',
                                                '&:hover': {
                                                    bgcolor: '#DD8748',
                                                    boxShadow: '0 15px 30px rgba(248, 158, 52, 0.3)',
                                                    transform: 'translateY(-3px)'
                                                }
                                            }}
                                            endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                        >
                                            {isSubmitting ? t('common.sending') : t('contact.form.submit')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Contact;